/**
 * relevanceChecker — given a candidate protein (PDB ID or UniProt) and a
 * domain term (a disease slug like 'breast_cancer'), decides whether the
 * candidate is relevant to that domain.
 *
 * Local-only: reads from `diseaseTargets.js` (and an optional
 * `targetAliases.js` if added later). Never makes network calls. The
 * `gibbs-clarify-on-ambiguous-target` study's `relevance-check-local-first`
 * criterion is the contract.
 *
 * @module gibbs/text-to-action/relevanceChecker
 */

import { diseaseTargets } from '../knowledge/diseaseTargets';

/**
 * @typedef {Object} RelevanceResult
 * @property {boolean} relevant
 * @property {string} evidence - non-empty string naming the local source
 *   (or, when relevant=false, naming the absence)
 */

/**
 * Look up a candidate protein in the topTargets of a disease. Tries PDB
 * ID match first (case-insensitive), then UniProt match (case-sensitive
 * — UniProt IDs are conventionally upper-case so a sloppy lookup is OK).
 *
 * @param {string} candidate - PDB ID like "4RMG" or UniProt like "P03372"
 * @param {Array<Object>} topTargets - the disease's topTargets array
 * @returns {{ index: number, target: Object } | null}
 *   the matching topTargets entry + its index, or null if no match
 * @private
 */
function findInTopTargets(candidate, topTargets) {
    if (!candidate || typeof candidate !== 'string') return null;
    if (!Array.isArray(topTargets)) return null;

    const candUpper = candidate.toUpperCase();

    for (let i = 0; i < topTargets.length; i += 1) {
        const target = topTargets[i];
        if (!target) continue;

        // UniProt match (typically upper-case in the data)
        if (typeof target.uniprot === 'string'
            && target.uniprot.toUpperCase() === candUpper) {
            return { index: i, target };
        }

        // PDB ID match — check the pdbIds array
        if (Array.isArray(target.pdbIds)) {
            for (const pdb of target.pdbIds) {
                if (typeof pdb === 'string' && pdb.toUpperCase() === candUpper) {
                    return { index: i, target };
                }
            }
        }
    }

    return null;
}

/**
 * Determine whether a candidate protein is relevant to a domain term.
 *
 * @param {string} candidate - PDB ID (e.g., "4RMG") or UniProt accession
 *   (e.g., "P03372"). Case-insensitive.
 * @param {string} domainTerm - disease slug (e.g., "breast_cancer")
 * @returns {RelevanceResult}
 */
export function check(candidate, domainTerm) {
    if (!candidate || typeof candidate !== 'string') {
        return { relevant: false, evidence: 'invalid_candidate' };
    }
    if (!domainTerm || typeof domainTerm !== 'string') {
        return { relevant: false, evidence: 'invalid_domain' };
    }

    const diseases = diseaseTargets && diseaseTargets.diseases;
    if (!diseases || typeof diseases !== 'object') {
        return { relevant: false, evidence: 'diseaseTargets_unavailable' };
    }

    const entry = diseases[domainTerm];
    if (!entry) {
        return { relevant: false, evidence: 'unknown_domain' };
    }

    const hit = findInTopTargets(candidate, entry.topTargets);
    if (!hit) {
        return {
            relevant: false,
            evidence: `not_in_diseaseTargets.${domainTerm}.topTargets`,
        };
    }

    const geneOrName = hit.target.geneSymbol
        || hit.target.name
        || hit.target.uniprot
        || candidate;
    return {
        relevant: true,
        evidence: `diseaseTargets.${domainTerm}.topTargets[${hit.index}] (${geneOrName})`,
    };
}

export default { check };
